/**
 * The SBOM Dependency-Track holds for one instance, side by side with the
 * configuration AssetRadar believes is installed.
 *
 * Showing both is the point. A component that appears in the CMDB configuration
 * but not in DT (or the other way round) is the usual explanation for a
 * suspiciously clean scan — a missing SBOM export, or a CPE identity DT cannot
 * match. Neither list alone makes that visible.
 */

import { useMemo, useState, type ReactNode } from 'react';

import Banner from '../../components/Banner';
import Chip, { ChipList } from '../../components/Chip';
import DataTable, { type DataTableColumn } from '../../components/DataTable';
import EmptyState from '../../components/EmptyState';
import { DASH, fmtNumber, fmtVersion } from '../../lib/format';
import type { DtComponent, DtSbomResponse } from '../../lib/types';

import { SEVERITY_LABELS, SEVERITY_TONE } from './severity';

export interface SbomPanelProps {
  data: DtSbomResponse;
}

/** Normalised name used to pair a DT component with a catalog version. */
function nameKey(value: string): string {
  return value
    .toLowerCase()
    .replace(/\s*\(firmware\)\s*$/, '')
    .trim();
}

export function SbomPanel({ data }: SbomPanelProps): ReactNode {
  const [search, setSearch] = useState('');
  // Pending metrics do not prevent a comparison once the SBOM components are
  // available. An unavailable SBOM must never turn every catalog entry into a gap.
  const canCompare =
    data.scan_state === 'ok' ||
    (data.scan_state === 'ausstehend' && data.components.length > 0);

  const rows = useMemo((): DtComponent[] => {
    const term = search.trim().toLowerCase();
    if (term === '') return data.components;
    return data.components.filter(
      (component) =>
        component.name.toLowerCase().includes(term) ||
        fmtVersion(component.version).toLowerCase().includes(term) ||
        (component.cpe ?? '').toLowerCase().includes(term),
    );
  }, [data.components, search]);

  /**
   * Which catalog versions have no counterpart in the uploaded SBOM. The match is
   * on the component *name* only: the SBOM builder emits an extra
   * "<name> (firmware)" sub-component per version, so a version match would
   * report false gaps.
   */
  const missingInDt = useMemo(() => {
    if (!canCompare) return [];
    const uploaded = new Set(data.components.map((component) => nameKey(component.name)));
    return data.katalog_versionen.filter((version) => !uploaded.has(nameKey(version.bezeichnung)));
  }, [canCompare, data.components, data.katalog_versionen]);

  const columns = useMemo(
    (): readonly DataTableColumn<DtComponent>[] => [
      {
        key: 'name',
        header: 'Komponente',
        sortValue: (row) => row.name.toLowerCase(),
        render: (row) => (
          <>
            <div>{row.name || DASH}</div>
            {row.group ? <div className="muted">{row.group}</div> : null}
          </>
        ),
      },
      {
        key: 'version',
        header: 'Version',
        width: '140px',
        sortValue: (row) => fmtVersion(row.version),
        render: (row) => <span className="mono">{fmtVersion(row.version)}</span>,
      },
      {
        key: 'findings',
        header: 'Befunde',
        width: '110px',
        align: 'right',
        sortValue: (row) => row.findings,
        render: (row) =>
          row.findings === 0 ? (
            <span className="muted">0</span>
          ) : (
            <Chip tone={SEVERITY_TONE[row.worst_severity ?? 'UNASSIGNED'] ?? 'neutral'}>
              {row.findings}
              {row.worst_severity ? ` · ${SEVERITY_LABELS[row.worst_severity]}` : ''}
            </Chip>
          ),
        // Worst severity decides ties so the riskiest component stays on top.
      },
      {
        key: 'identity',
        header: 'CPE',
        sortValue: (row) => (row.cpe ?? '').toLowerCase(),
        render: (row) =>
          row.cpe ? (
            <code className="mono">{row.cpe}</code>
          ) : (
            <span className="muted" title="Keine CPE angegeben">
              {DASH}
            </span>
          ),
      },
    ],
    [],
  );

  if (!canCompare) {
    return (
      <div className="card-body">
        <Banner
          kind={data.scan_state === 'unbekannt' ? 'error' : 'info'}
          sub="Der Abgleich mit der Konfiguration ist möglich, sobald SBOM-Daten vorliegen."
        >
          {data.note ??
            (data.scan_state === 'unbekannt'
              ? 'Die SBOM-Daten konnten nicht geladen werden.'
              : 'Für diese Instanz sind noch keine SBOM-Daten verfügbar.')}
        </Banner>
        <KatalogList data={data} missing={missingInDt} />
      </div>
    );
  }

  return (
    <>
      <div className="table-toolbar">
        <input
          type="search"
          value={search}
          placeholder="Komponente, Version, CPE…"
          aria-label="SBOM durchsuchen"
          onChange={(event) => setSearch(event.target.value)}
        />
        <div className="table-toolbar-options">
          <span className="table-count" aria-live="polite">
            {rows.length === data.components.length
              ? `${fmtNumber(rows.length)} ${rows.length === 1 ? 'Komponente' : 'Komponenten'} in Dependency-Track`
              : `${fmtNumber(rows.length)} von ${fmtNumber(data.components.length)} Komponenten in Dependency-Track`}
          </span>
        </div>
      </div>

      <div className="card-body">
      {missingInDt.length > 0 ? (
        <Banner
          kind="warn"
          sub="Führen Sie den SBOM-Export erneut aus, damit Dependency-Track diese Komponenten prüfen kann."
          items={missingInDt.map((version) => version.label)}
        >
          {missingInDt.length === 1
            ? '1 Komponentenversion aus der Konfiguration fehlt im hochgeladenen SBOM:'
            : `${missingInDt.length} Komponentenversionen aus der Konfiguration fehlen im hochgeladenen SBOM:`}
        </Banner>
      ) : null}

      <div className="vuln-compare">
        <div>
          <h3>Im SBOM (Dependency-Track)</h3>
          {rows.length === 0 ? (
            <EmptyState
              compact
              title={data.components.length === 0
                ? 'Das hochgeladene SBOM enthält keine Komponenten'
                : 'Keine Komponente passt zur Suche'}
            />
          ) : (
            <DataTable
              rows={rows}
              columns={columns}
              rowKey={(row) => `${row.name}@${row.version}`}
              pageSize={10}
              paginationResetKey={JSON.stringify([data.instanz_id, search])}
              resetPageOnRowsChange={false}
              caption="Komponenten im SBOM"
            />
          )}
        </div>
        <div>
          <KatalogList data={data} missing={missingInDt} />
        </div>
      </div>
      </div>
    </>
  );
}

/** The CMDB side of the comparison: what AssetRadar says runs on the instance. */
function KatalogList({
  data,
  missing,
}: {
  data: DtSbomResponse;
  missing: readonly { id: number }[];
}): ReactNode {
  const missingIds = new Set(missing.map((entry) => entry.id));
  return (
    <>
      <h3>Laut Konfiguration (AssetRadar)</h3>
      {data.katalog_versionen.length === 0 ? (
        <EmptyState compact title="Keine Konfiguration auflösbar">
          Der Instanz fehlt eine Produktversion mit Stückliste.
        </EmptyState>
      ) : (
        <ul className="vuln-katalog">
          {data.katalog_versionen.map((version) => (
            <li key={version.id}>
              <span>{version.bezeichnung}</span>
              <span className="mono muted">{fmtVersion(version.version)}</span>
              <ChipList>
                {version.abweichung ? (
                  <Chip tone="info" title="Nicht in der Basisversion dieser Kundenversion enthalten">
                    Kundenversion
                  </Chip>
                ) : null}
                {version.kev_betroffen ? (
                  <Chip tone="alert" title={version.kev_cves.join(', ')}>
                    KEV
                  </Chip>
                ) : null}
                {missingIds.has(version.id) ? <Chip tone="warn">nicht im SBOM</Chip> : null}
              </ChipList>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

export default SbomPanel;
